import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { PilotButton } from "./PilotIntake";

const NAV_LINKS = [
  { to: "/", label: "Home", key: "home" },
  { to: "/product-detail", label: "Product", key: "product-detail" },
  { to: "/why-choose-us", label: "Why CoursEngage", key: "why-choose-us" },
  { to: "/pricing", label: "Pricing", key: "pricing" },
  { to: "/contact", label: "Contact", key: "contact" },
];

function Navbar({ theme, toggleTheme }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const activeKey = (() => {
    const map = {
      "/pricing": "pricing",
      "/product-detail": "product-detail",
      "/why-choose-us": "why-choose-us",
      "/contact": "contact",
    };
    return map[location.pathname] || "home";
  })();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        className="ce-site-nav fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: scrolled
            ? "color-mix(in srgb, var(--bg-color) 85%, transparent)"
            : "transparent",
          backdropFilter: scrolled ? "saturate(140%) blur(14px)" : "none",
          WebkitBackdropFilter: scrolled ? "saturate(140%) blur(14px)" : "none",
          borderBottom: scrolled
            ? "1px solid var(--border-color)"
            : "1px solid transparent",
        }}
      >
        <div className="ce-site-nav-inner max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img
              src="/brand-assets/logo-icon.svg"
              alt=""
              aria-hidden="true"
              width="32"
              height="32"
              className="w-8 h-8 rounded-lg"
            />
            <span className="text-lg font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
              CoursEngage
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => {
              const active = activeKey === l.key;
              return (
                <Link
                  key={l.key}
                  to={l.to}
                  className="px-3 py-2 text-sm rounded-full transition-colors"
                  style={{
                    color: active ? "var(--primary-color)" : "var(--text-soft)",
                    fontWeight: active ? 600 : 500,
                    backgroundColor: active ? "var(--primary-50)" : "transparent",
                  }}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={toggleTheme}
              className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors"
              style={{
                color: "var(--text-soft)",
                backgroundColor: "transparent",
                border: "1px solid var(--border-color)",
              }}
              title="Toggle theme"
              aria-label="Toggle theme"
            >
              {theme === "light" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="4" strokeWidth="2"/><path strokeLinecap="round" strokeWidth="2" d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
              )}
            </button>

            <Link
              to="/sign-in"
              className="hidden sm:inline-flex text-sm font-medium px-3 py-2 rounded-full transition-colors"
              style={{ color: "var(--text-soft)" }}
            >
              Sign in
            </Link>

            <PilotButton className="ce-btn ce-btn-primary text-sm">
              Join pilot
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </PilotButton>

            {/* Mobile menu toggle */}
            <button
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-full"
              style={{ color: "var(--text-color)", border: "1px solid var(--border-color)" }}
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
            >
              {open ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M6 18L18 6"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {open && (
          <div
            className="lg:hidden border-t"
            style={{
              borderColor: "var(--border-color)",
              backgroundColor: "var(--card-bg)",
            }}
          >
            <div className="max-w-7xl mx-auto px-5 py-3 flex flex-col">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.key}
                  to={l.to}
                  className="py-2.5 text-base"
                  style={{
                    color: activeKey === l.key ? "var(--primary-color)" : "var(--text-color)",
                    fontWeight: activeKey === l.key ? 600 : 500,
                  }}
                >
                  {l.label}
                </Link>
              ))}
              <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: "1px solid var(--border-color)" }}>
                <Link to="/sign-in" className="ce-btn ce-btn-secondary flex-1 text-sm">Sign in</Link>
                <PilotButton className="ce-btn ce-btn-primary flex-1 text-sm">Join pilot</PilotButton>
              </div>
            </div>
          </div>
        )}
      </header>
      <div className="ce-site-nav-spacer" />
    </>
  );
}

export default Navbar;
